/**
 * Карта портфелю на Windy Map Forecast API: жива анімована погода Windy плюс
 * НАШІ станції мітками зверху.
 *
 * ЧОМУ САМЕ ЦЕЙ ШЛЯХ. Windy-iframe міток не приймає взагалі (чужий домен),
 * тайли хмарності без ключа дають лише статичну картинку, а власний рендерер
 * частинок довелось би супроводжувати самим. JS API Windy побудований на
 * Leaflet, тож його карта — звичайний L.Map: на неї лягають наші маркери,
 * попапи й будь-які шари.
 *
 * КОНФЛІКТ LEAFLET (головна пастка). libBoot.js не містить свого Leaflet і
 * вимагає глобальний L саме версії 1.4.x. Наш бандл реєструє window.L = 1.9.x
 * (react-leaflet), тож перед завантаженням Windy глобал прибираємо й тягнемо
 * 1.4.0 з CDN. На react-leaflet це не впливає: ті компоненти імпортують
 * ES-модуль, а не глобал. Саме через цю перевірку версії попередня спроба
 * падала з «Wrong version of Leaflet library».
 *
 * ШАР ПОГОДИ не зашиваємо: список дозволених шарів залежить від тарифу ключа
 * (на нашому 'clouds' раніше відхилявся з «Invalid value for overlay», і це
 * був відхилений проміс, який обривав ініціалізацію всього UI). Тому беремо
 * store.getAllowed('overlay') і обираємо перший доступний зі списку переваг.
 *
 * КЛЮЧ прив'язаний до домену в кабінеті Windy. Для cabinet2 він доданий; для
 * прод-домену його треба буде додати окремо, інакше карта віддасть 403.
 */
import i18n from '@/i18n';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, RotateCcw } from 'lucide-react';
import type { StationLive } from './overviewTypes';
import { stationStatus, fmtMw } from './overviewTypes';

const WINDY_API_KEY = (import.meta.env.VITE_WINDY_API_KEY as string | undefined) ?? '';

/** Кишинів — точка, навколо якої центрується огляд портфелю. */
const CHISINAU: [number, number] = [47.0105, 28.8638];

// Порядок переваг: для СЕС найкорисніша хмарність, далі вітер як запасний —
// він доступний на будь-якому тарифі й дає впізнаваний рух частинок.
const OVERLAY_PREFERENCE = ['clouds', 'cloudtop', 'wind'];

// Підписи шарів нашою мовою: меню Windy ми прибрали, а голі англійські
// ідентифікатори ('clouds', 'temp') користувачу нічого не кажуть.
const OVERLAY_LABEL: Record<string, string> = {
  get clouds() { return i18n.t('ak_home_clouds'); },
  get cloudtop() { return i18n.t('ak_home_cloud_tops'); },
  get wind() { return i18n.t('ak_home_wind'); },
  get gust() { return i18n.t('ak_home_gusts'); },
  get temp() { return i18n.t('ak_home_temperature'); },
  get rain() { return i18n.t('ak_home_precipitation'); },
  get rainAccu() { return i18n.t('ak_home_total_precipitation'); },
  get pressure() { return i18n.t('ak_home_pressure'); },
};
// Показуємо лише ті, що мають сенс для СЕС, і лише дозволені ключу.
const OVERLAY_PILLS = ['clouds', 'wind', 'temp', 'rain'];

// Windy визначає мову сам — за браузером і геолокацією, і для України
// зазвичай віддає російську. Тому задаємо явно й беремо з перемикача
// кабінету: карта має говорити тією ж мовою, що й решта інтерфейсу.
const WINDY_LANG: Record<string, string> = { uk: 'uk', pl: 'pl', en: 'en' };

/**
 * Порядок накладання шарів Windy у вбудованому контейнері.
 *
 * У lib.css Windy полотно погоди стоїть на `#windy .leaflet-map-pane canvas
 * { z-index: 100 }`, а його контроли позиціоновані `position: fixed` і майже
 * без z-index. На windy.com це працює, бо fixed виносить їх у систему
 * координат ВІКНА — поза стек-контекст карти, тож вони завжди зверху.
 *
 * Наш контейнер має клас .tile, а той — backdrop-filter. Ця властивість
 * одночасно створює обмежувальний блок для fixed і власний стек-контекст.
 * Через це контроли лишаються всередині плитки й починають конкурувати з
 * полотном за z-index — і програють. Під час зуму полотно перемальовується,
 * на мить зникає, і контроли визирають: саме той ефект «блимнули й сховались».
 *
 * Тому: піднімаємо UI Windy над усіма шарами карти (полотно 100, svg 200,
 * overlay 400, маркери 600, попапи 700, leaflet-control 800 — беремо 850),
 * прив'язуємо нижню панель до контейнера замість вікна й прибираємо
 * margin-right:320px, зарезервований під правий сайдбар windy.com, якого в
 * embed немає.
 */
const WINDY_UI_CSS_ID = 'windy-embed-ui-css';
function ensureWindyUiCss() {
  if (document.getElementById(WINDY_UI_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = WINDY_UI_CSS_ID;
  style.textContent = `
    #windy #bottom,
    #windy #embed-zoom,
    #windy #logo-wrapper,
    #windy #plugins,
    #windy #legend,
    #windy #legend-mobile,
    #windy #mobile-ovr-select,
    #windy #accumulations { z-index: 850 !important; }

    #windy #bottom {
      position: absolute !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      margin-right: 0 !important;
    }
    #windy #logo-wrapper { position: absolute !important; }

    /* Windy тримає панелі на opacity:0 і проявляє їх лише під час руху миші
       над картою (клас .shy). Для плитки дашборда вони мають бути видимі
       постійно — наводити курсор, щоб побачити легенду, тут недоречно. */
    #windy .shy { opacity: 1 !important; }

    /* ПАСТКА МЕНЮ. Клік по назві шару («Вітер») додає body клас .onmenu, а
       той у lib.css ховає ВСІ контроли:
         .onmenu #windy #bottom { opacity: 0 }
         .onmenu #windy #embed-zoom { display: none }
       Саме меню розраховане на повноекранний windy.com і в embed не
       рендериться — виходу з цього стану немає, крім перезавантаження.
       Тому: тригер прибираємо зовсім, а правила .onmenu знешкоджуємо, щоб
       карта не могла застрягти в режимі меню з будь-якої іншої причини.
       Перемикач шарів натомість наш власний — нижче в розмітці. */
    #windy #mobile-ovr-select { display: none !important; }

    /* Зум Windy стоїть right:8/top:35 — рівно під нашим перемикачем шарів
       (він теж праворуч угорі, висота ~34px). Зсуваємо нижче, щоб не
       накладались. */
    #windy #embed-zoom { top: 52px !important; }
    body.onmenu #windy #bottom { opacity: 1 !important; }
    body.onmenu #windy #embed-zoom { display: flex !important; }
  `;
  document.head.appendChild(style);
}

const LEAFLET_CSS_ID = 'leaflet-1-4-0-css';
function ensureLeafletCss() {
  if (document.getElementById(LEAFLET_CSS_ID)) return;
  const link = document.createElement('link');
  link.id = LEAFLET_CSS_ID;
  link.rel = 'stylesheet';
  link.href = 'https://unpkg.com/leaflet@1.4.0/dist/leaflet.css';
  document.head.appendChild(link);
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Script failed to load: ${src}`));
    document.head.appendChild(s);
  });
}

/**
 * Скільки чекати на ініціалізацію, перш ніж визнати спробу невдалою.
 *
 * Windy автентифікується ОДНИМ запитом на старті й іншого шансу не дає: якщо
 * той запит не пройшов, карта лишається мертвою до перезавантаження сторінки.
 * Сам він при цьому затирає наш контейнер власною червоною плашкою — без
 * кнопки, без нашого оформлення. Тому стан помилки тримаємо свій.
 *
 * 12 с — із запасом на повільний канал: успішна ініціалізація вкладається в
 * одиниці секунд, а лишній відлік бачить лише той, у кого вже все погано.
 */
const WATCHDOG_MS = 12000;

type MapStatus = 'loading' | 'ready' | 'error';

let windyScriptPromise: Promise<void> | null = null;
function loadWindyScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if ((window as any).windyInit) return Promise.resolve();
  if (windyScriptPromise) return windyScriptPromise;
  windyScriptPromise = (async () => {
    try { delete (window as any).L; } catch { (window as any).L = undefined; }
    ensureLeafletCss();
    await loadScript('https://unpkg.com/leaflet@1.4.0/dist/leaflet.js');
    await loadScript('https://api.windy.com/assets/map-forecast/libBoot.js');
  })();
  return windyScriptPromise;
}

/**
 * Мова карти — ЯВНО через store, а не лише опцією windyInit.
 *
 * Опція `lang` у windyInit потрапляє в store як store.setDefault(), тобто
 * ЛИШЕ типове значення. Але Windy зберігає `lang` у localStorage (save:true),
 * і збережене там "auto" перекриває наш default; "auto" = мова браузера, тож
 * на російському Chrome підписи міст і весь інтерфейс карти були російські —
 * попри lang:'uk' в опціях.
 *
 * Шар підписів міст — окремі тайли tiles-s.windy.com/labels/v1.4/{usedLang},
 * а usedLang виставляється з події store 'lang'. Тому store.set('lang') —
 * єдиний надійний важіль: Windy перекладає інтерфейс і перевантажує підписи.
 * englishLabels вимикаємо явно, бо з ним шар бере "en" незалежно від мови.
 */
function applyLang(api: any, lang: string) {
  try {
    const store = api?.store;
    if (!store) return;
    // forceChange — щоб подія пішла навіть коли значення формально збігається:
    // store.set без нього мовчить при однаковому значенні, а usedLang при цьому
    // міг лишитися мовою браузера, бо trans обчислює його ще до нашого set.
    store.set('englishLabels', false, { forceChange: true });
    store.set('lang', lang, { forceChange: true });
    // usedLang — саме той ключ, на який підписаний шар підписів міст
    // (labels/v1.4/{usedLang}); виставляємо його прямо, не чекаючи ланцюжка.
    store.set('usedLang', lang, { forceChange: true });
  } catch { /* без мови карта однаково працює — не критично */ }
}

/**
 * Windy зберігає свої налаштування в localStorage під ключами settings_*.
 * Збережена там мова («auto» або «ru», якщо її колись обрали в самому Windy)
 * має пріоритет над опцією windyInit. Тому перед ініціалізацією ці ключі
 * прибираємо — мову карти визначає кабінет, а не те, що Windy запам'ятав.
 */
function clearWindyLangStorage() {
  try {
    for (const k of ['settings_lang', 'settings_lang_ts', 'settings_englishLabels',
      'settings_englishLabels_ts', 'settings_usedLang', 'settings_usedLang_ts']) {
      window.localStorage.removeItem(k);
    }
  } catch { /* приватний режим без localStorage — пропускаємо */ }
}

/** Резолв токен-кольору статусу в hex: SVG-мітка не читає var(). */
function resolveColor(v: string): string {
  const m = v.match(/var\((--[\w-]+)\)/);
  if (!m || typeof window === 'undefined') return v;
  return getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim() || '#8A8A93';
}

const pinHtml = (color: string) =>
  `<svg width="20" height="27" viewBox="0 0 20 27" xmlns="http://www.w3.org/2000/svg">`
  + `<path d="M10 0C4.5 0 0 4.5 0 10c0 7.5 10 17 10 17s10-9.5 10-17C20 4.5 15.5 0 10 0z"`
  + ` fill="${color}" stroke="#fff" stroke-width="1.6"/>`
  + `<circle cx="10" cy="10" r="3.6" fill="#fff"/></svg>`;

export default function WindyPortfolioMap({ stations, height = 244, className = '' }:
  { stations: StationLive[]; height?: number | string; className?: string }) {
  const { i18n } = useTranslation();
  const lang = WINDY_LANG[i18n.language?.slice(0, 2) ?? 'uk'] ?? 'uk';
  const langRef = useRef(lang);
  langRef.current = lang;

  const apiRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  // Доступні шари приходять від Windy після ініціалізації — до того
  // перемикач не рендеримо, щоб не пропонувати недоступне.
  const [overlays, setOverlays] = useState<string[]>([]);
  const [overlay, setOverlay] = useState<string>('');
  const stationsRef = useRef(stations);
  stationsRef.current = stations;

  const [status, setStatus] = useState<MapStatus>('loading');
  // Лічильник спроб — він же ключ ефекту: «Спробувати ще» просто збільшує
  // його, і вся ініціалізація проходить наново без перезавантаження сторінки.
  const [attempt, setAttempt] = useState(0);

  // Ініціалізація Windy — РІВНО ОДИН раз на спробу. Повторний windyInit на
  // тому ж контейнері лишає попередню карту живою і з'їдає пам'ять.
  useEffect(() => {
    let alive = true;
    let watchdog: number | undefined;
    let probe: number | undefined;
    const stopTimers = () => {
      if (watchdog) window.clearTimeout(watchdog);
      if (probe) window.clearInterval(probe);
    };

    ensureWindyUiCss();
    setStatus('loading');

    // Плашку Windy з попередньої спроби прибираємо самі: вона лежить прямо в
    // нашому контейнері, і без очищення нова карта малювалась би поверх неї.
    const host = document.getElementById('windy');
    if (host && attempt > 0) host.innerHTML = '';

    const fail = () => {
      if (!alive) return;
      stopTimers();
      // Якщо впав сам скрипт — знімаємо кеш промісу, інакше повтор одразу
      // впреться у збережену відмову. Коли скрипт уже завантажився,
      // loadWindyScript однаково поверне готовий результат (перевірка
      // window.windyInit на вході), тож зайвих тегів не з'явиться.
      windyScriptPromise = null;
      setStatus('error');
    };

    watchdog = window.setTimeout(fail, WATCHDOG_MS);
    // Windy малює свою помилку в наш контейнер із характерним фоновим
    // зображенням api-error. Ловимо її одразу, щоб не тримати користувача
    // перед порожнім прямокутником усі 12 секунд.
    probe = window.setInterval(() => {
      const el = document.getElementById('windy');
      if (el && el.innerHTML.includes('api-error')) fail();
    }, 500);

    loadWindyScript()
      .then(() => {
        if (!alive) return;
        const windyInit = (window as any).windyInit;
        if (!windyInit || !WINDY_API_KEY) { fail(); return; }

        // Збережена Windy мова не має перекривати мову кабінету.
        clearWindyLangStorage();

        windyInit(
          { key: WINDY_API_KEY, lat: CHISINAU[0], lon: CHISINAU[1], zoom: 6, lang: langRef.current },
          (windyAPI: any) => {
            if (!alive) return;
            stopTimers();
            setStatus('ready');
            apiRef.current = windyAPI;

            applyLang(windyAPI, langRef.current);

            // Шар обираємо зі списку, дозволеного ключу, а не навмання.
            try {
              const allowed: string[] = windyAPI.store.getAllowed('overlay') ?? [];
              setOverlays(OVERLAY_PILLS.filter((o) => allowed.includes(o)));
              const pick = OVERLAY_PREFERENCE.find((o) => allowed.includes(o));
              if (pick) {
                windyAPI.store.set('overlay', pick);
                setOverlay(pick);
              } else {
                setOverlay(windyAPI.store.get('overlay') ?? '');
              }
            } catch { /* шар лишиться типовим — не критично */ }

            drawStations();
          },
        );
      })
      .catch(fail);

    return () => { alive = false; stopTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Маркери — окремим шаром, щоб перемальовувати їх при оновленні телеметрії
  // без переініціалізації карти (вона важка й перезапускає анімацію погоди).
  const drawStations = () => {
    const api = apiRef.current;
    const L = (window as any).L;
    if (!api?.map || !L) return;

    if (layerRef.current) {
      api.map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    const pts = stationsRef.current.filter((s) => s.latitude != null && s.longitude != null);
    if (!pts.length) return;

    const group = L.layerGroup();
    pts.forEach((s) => {
      const st = stationStatus(s);
      const icon = L.divIcon({
        className: '',
        html: pinHtml(resolveColor(st.color)),
        iconSize: [20, 27],
        iconAnchor: [10, 27],
      });
      L.marker([s.latitude as number, s.longitude as number], { icon })
        .bindPopup(`<b>${s.name}</b><br>${fmtMw(s.pacKw)} ${i18n.t('ak_home_mw')} · ${st.label}`)
        .addTo(group);
    });
    group.addTo(api.map);
    layerRef.current = group;

    // Кишинів лишається центром: у межі додаємо дзеркальні відображення
    // станцій відносно нього — прямокутник стає симетричним, усі точки
    // всередині. Звичайний fitBounds зсунув би центр до середини станцій.
    const [cLat, cLon] = CHISINAU;
    const lats = pts.flatMap((s) => [s.latitude as number, 2 * cLat - (s.latitude as number)]);
    const lons = pts.flatMap((s) => [s.longitude as number, 2 * cLon - (s.longitude as number)]);
    api.map.fitBounds(
      [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]],
      { padding: [24, 24] },
    );
  };

  // Оновлення станцій (телеметрія тягнеться раз на 30 с) перемальовує лише
  // шар міток.
  useEffect(() => { drawStations(); /* eslint-disable-next-line */ }, [stations]);

  // Перемикач мови кабінету → мова карти, без переініціалізації: Windy сам
  // перекладає інтерфейс і перевантажує шар підписів за подією store.
  useEffect(() => { if (apiRef.current) applyLang(apiRef.current, lang); }, [lang]);

  return (
    <div className={`tile overflow-hidden relative ${className}`} style={{ padding: 0 }}>
      {/* Windy очікує контейнер саме з id="windy" — так вимагає libBoot. */}
      <div id="windy" style={{ height, width: '100%' }} />

      {/* Власний перемикач шарів замість меню Windy (те вмикає режим .onmenu
          і ховає всі контроли без шляху назад). z-index вище за UI Windy,
          який ми підняли на 850. Формат пілюль — як у хедер-контролях
          кабінету: 28px, radius 8. */}
      {status === 'ready' && overlays.length > 1 && (
        <div className="absolute flex" style={{
          top: 8, right: 8, zIndex: 900, gap: 4, padding: 3, borderRadius: 10,
          background: 'var(--surface-solid)', border: '1px solid var(--line)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>
          {overlays.map((o) => {
            const active = o === overlay;
            return (
              <button
                key={o}
                type="button"
                onClick={() => {
                  try {
                    apiRef.current?.store.set('overlay', o);
                    setOverlay(o);
                  } catch { /* недоступний шар — стан не міняємо */ }
                }}
                style={{
                  height: 28, borderRadius: 8, padding: '0 10px', cursor: 'pointer',
                  border: 'none', whiteSpace: 'nowrap', fontSize: 11,
                  background: active ? 'var(--surface-2)' : 'transparent',
                  color: active ? 'var(--text)' : 'var(--text-muted)',
                  fontWeight: active ? 600 : 400,
                }}
              >
                {OVERLAY_LABEL[o] ?? o}
              </button>
            );
          })}
        </div>
      )}

      {/* Власний стан помилки. Непрозорий — накриває плашку Windy, яку той
          вписує просто в наш контейнер: інакше поверх нашого тексту світився
          б його червоний банер англійською.

          Формулювання обережне навмисне: найчастіше це не «карта зламалась»,
          а один невдалий запит автентифікації на старті — мережа, блокувальник
          у браузері чи коротка недоступність Windy. Ключ і домен тут ні до
          чого, і писати «немає доступу» було б хибним діагнозом. */}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ gap: 10, background: 'var(--surface-solid)', zIndex: 950, padding: 16 }}>
          <CloudOff size={22} strokeWidth={1.7} style={{ color: 'var(--err)' }} />
          <div style={{ fontSize: 13, color: 'var(--text)', textAlign: 'center' }}>
            {i18n.t('ak_home_the_weather_map_failed_to_load')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', textAlign: 'center', maxWidth: 300 }}>
            {i18n.t('ak_home_the_windy_service_did_not_respond_the')}
          </div>
          <button
            type="button"
            onClick={() => setAttempt((a) => a + 1)}
            className="flex items-center cursor-pointer"
            style={{
              gap: 6, height: 28, borderRadius: 8, padding: '0 12px', marginTop: 2,
              background: 'var(--surface-2)', border: '1px solid var(--line-strong)',
              color: 'var(--text)', fontSize: 12, fontWeight: 600,
            }}
          >
            <RotateCcw size={13} strokeWidth={2} />
            {i18n.t('ak_home_try_again')}
          </button>
        </div>
      )}
    </div>
  );
}
